/**
 * Device classification, expressed as media queries and one pure resolver.
 *
 * Split out of `useResponsiveView` so the decision itself is testable without a DOM: the hook
 * evaluates the queries, this module decides what they mean.
 */

/**
 * "Render the phone layout." Width alone cannot answer this — a phone held sideways is 844-956px
 * wide (iPhone 15/16 Pro Max landscape is 932/956px), well past any sane width threshold. The
 * second clause is what actually catches those: short AND touch.
 *
 * The width clause sits at MUI's `md` (899.95px) rather than `sm`, so the whole app classifies on
 * one number. Nothing real lives in the 600-900 band — no tablets in this user base — so the band
 * is narrowed desktop windows, which are better served by the mobile layout anyway.
 */
export const MOBILE_MEDIA_QUERY = "(max-width: 899.95px), (max-height: 500px) and (pointer: coarse)";

/**
 * "The phone is on its side." Height is what forces a side sheet rather than a bottom one — a
 * landscape desktop window has plenty of it — but height and orientation alone are not enough to
 * mean "phone": `orientation: landscape` is only "wider than tall", which every desktop window is,
 * and a desktop viewport can easily fall under 500px tall with devtools docked to the bottom or a
 * window snapped to a short region. Without the pointer clause those became `mobile-landscape` at
 * any width. The clause matches `MOBILE_MEDIA_QUERY`'s short-viewport arm, so a match here always
 * implies a match there.
 */
export const LANDSCAPE_MEDIA_QUERY = "(max-height: 500px) and (orientation: landscape) and (pointer: coarse)";

/** Which layout tree renders. Three states rather than two independent booleans, because
 *  `!isMobile && isLandscape` has no meaning and would eventually be branched on. */
export type ResponsiveLayout = "desktop" | "mobile-portrait" | "mobile-landscape";

export interface ResponsiveMatches {
	/** `MOBILE_MEDIA_QUERY` matched. */
	narrowOrShort: boolean;
	/** `LANDSCAPE_MEDIA_QUERY` matched. */
	shortLandscape: boolean;
}

/**
 * A short landscape viewport implies mobile even when the WIDTH clause misses it — that is the
 * whole point of the second query, since the widest phones clear 900px on their side. Both queries
 * carry `pointer: coarse`, so this cannot promote a desktop window.
 */
export function resolveResponsiveLayout({ narrowOrShort, shortLandscape }: ResponsiveMatches): ResponsiveLayout {
	if (shortLandscape) return "mobile-landscape";
	return narrowOrShort ? "mobile-portrait" : "desktop";
}
